import { unparse } from 'papaparse';

import { NOT_PROVIDED } from '~pages/classes/bulk-upload/types';

/**
 * Download rows as a CSV file. Rows are expected to already be mapped to the
 * Enrollio internal column structure, so studios can review/correct their data
 * before importing (QA 4.1). A BOM is prepended for correct Excel encoding.
 */
export function downloadMappedCsv(
  rows: Array<Record<string, unknown>>,
  filename: string,
): void {
  downloadCsvBlob(unparse(rows), filename);
}

/**
 * Enrollio field headers that currently have no source-column mapping
 * (empty or "(Not provided)"). These are appended to the original CSV so
 * studios can fill them and re-upload.
 */
export function collectUnmappedClassHeadersToAppend(
  mappingRows: ReadonlyArray<{ header: string; selectedColumn: string }>,
): Array<string> {
  const seen = new Set<string>();
  const out: Array<string> = [];

  for (const row of mappingRows) {
    const selected = row.selectedColumn?.trim() ?? '';
    const hasRealMapping = selected.length > 0 && selected !== NOT_PROVIDED;
    if (hasRealMapping) continue;

    const key = row.header.trim();
    if (!key) continue;
    const lower = key.toLowerCase();
    if (seen.has(lower)) continue;
    seen.add(lower);
    out.push(key);
  }

  return out;
}

/**
 * Keep every original CSV column (and any extra keys present on rows), then
 * append empty columns for headers that are not already present.
 */
export function buildCsvWithAppendedColumns(
  rows: Array<Record<string, unknown>>,
  existingHeaders: Array<string>,
  columnsToAppend: ReadonlyArray<string>,
): { headers: Array<string>; rows: Array<Record<string, unknown>> } {
  const orderedOriginal: Array<string> = [];
  const seenLower = new Set<string>();

  const pushOriginal = (header: string) => {
    const key = header.trim();
    if (!key) return;
    const lower = key.toLowerCase();
    if (seenLower.has(lower)) return;
    seenLower.add(lower);
    orderedOriginal.push(key);
  };

  for (const header of existingHeaders) {
    pushOriginal(header);
  }
  // Preserve any original columns present on rows but missing from the header list.
  for (const row of rows) {
    for (const key of Object.keys(row)) {
      pushOriginal(key);
    }
  }

  const appended = columnsToAppend.filter((col) => {
    const key = col.trim();
    return key.length > 0 && !seenLower.has(key.toLowerCase());
  });
  const headers = [...orderedOriginal, ...appended];

  const nextRows = rows.map((row) => {
    const out: Record<string, unknown> = {};
    for (const header of headers) {
      const value = row[header];
      out[header] = value == null ? '' : value;
    }
    return out;
  });

  return { headers, rows: nextRows };
}

/**
 * Download the original CSV with empty columns appended for every Enrollio
 * field that has no mapping yet. After filling those cells, re-upload.
 */
export function downloadCsvWithAppendedColumns(
  rows: Array<Record<string, unknown>>,
  existingHeaders: Array<string>,
  columnsToAppend: ReadonlyArray<string>,
  filename: string,
): void {
  const { headers, rows: nextRows } = buildCsvWithAppendedColumns(
    rows,
    existingHeaders,
    columnsToAppend,
  );
  const csv = unparse({ fields: headers, data: nextRows });
  downloadCsvBlob(csv, filename);
}

function downloadCsvBlob(csv: string, filename: string): void {
  const safeName = filename.toLowerCase().endsWith('.csv')
    ? filename
    : `${filename}.csv`;
  const blob = new Blob([`\ufeff${csv}`], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = safeName;
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Fetch a sample template and re-download as a Blob with explicit CSV MIME
 * type + `.csv` filename so browsers/OS don't mislabel it (e.g. as XML).
 */
export async function downloadSampleCsvFromUrl(
  url: string,
  filename: string,
): Promise<void> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to download sample CSV (${response.status})`);
  }
  const text = await response.text();
  // Reject HTML/XML error pages masquerading as the template.
  const trimmed = text.trimStart();
  if (
    trimmed.startsWith('<!DOCTYPE') ||
    trimmed.startsWith('<html') ||
    trimmed.startsWith('<?xml') ||
    trimmed.startsWith('<')
  ) {
    throw new Error('Sample file was not a CSV');
  }
  downloadCsvBlob(text.replace(/^\uFEFF/, ''), filename);
}
